import React, { useMemo, useState } from "react";
import PendingInventoryDrawer from "../../AdminInventoryDashboard/PendingInventoryDrawer";

const getStockStatus = (availableParts, minStock) => {
  if (availableParts <= 0) return "OUT_OF_STOCK";
  if (minStock != null && availableParts <= minStock) return "LOW_STOCK";
  return "IN_STOCK";
};

const getStatusLabel = (status) => {
  switch (status) {
    case "OUT_OF_STOCK":
      return "Out of Stock";
    case "LOW_STOCK":
      return "Low Stock";
    case "IN_STOCK":
    default:
      return "In Stock";
  }
};

const getStatusClasses = (status) => {
  switch (status) {
    case "OUT_OF_STOCK":
      return "bg-red-900/40 text-red-300 border-red-500/60";
    case "LOW_STOCK":
      return "bg-amber-900/40 text-amber-300 border-amber-500/60";
    case "IN_STOCK":
    default:
      return "bg-emerald-900/40 text-emerald-300 border-emerald-500/60";
  }
};

/**
 * Props:
 *  - inventories: array of {
 *      id,
 *      name,
 *      items or item,
 *      parts,
 *      usage,
 *      minStock
 *    }
 *  - employeeData?: { fullName?: string }
 */
const InventoryTotalSummary = ({ inventories = [], employeeData }) => {
  const [search, setSearch] = useState("");
  const [stockFilter, setStockFilter] = useState("ALL"); // ALL | IN_STOCK | LOW_STOCK | OUT_OF_STOCK
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const handleSearchChange = (e) => setSearch(e.target.value);

  const enrichedInventories = useMemo(() => {
    return inventories.map((inv) => {
      const itemCount =
        inv.items != null ? inv.items : inv.item != null ? inv.item : 0;
      const partsPerItem = inv.parts ?? 0;
      const usage = inv.usage ?? 0;
      const minStock = inv.minStock ?? 0;

      let availableParts = itemCount * partsPerItem - usage;
      if (availableParts < 0) availableParts = 0;

      const stockStatus = getStockStatus(availableParts, minStock);

      return {
        ...inv,
        itemCount,
        partsPerItem,
        usage,
        minStock,
        availableParts,
        stockStatus,
      };
    });
  }, [inventories]);

  const filteredInventories = useMemo(() => {
    const term = search.trim().toLowerCase();

    return enrichedInventories.filter((inv) => {
      const matchesSearch = term
        ? (inv.name || "").toLowerCase().includes(term)
        : true;

      const matchesStock =
        stockFilter === "ALL" ? true : inv.stockStatus === stockFilter;

      return matchesSearch && matchesStock;
    });
  }, [enrichedInventories, search, stockFilter]);

  if (!inventories || inventories.length === 0) {
    return (
      <div className="w-full flex items-center justify-center py-16 bg-gray-50">
        <div className="max-w-md text-center bg-white rounded-2xl shadow border border-gray-200 px-6 py-8">
          <p className="text-sm text-gray-500 mb-2">Total Inventory</p>
          <p className="text-lg font-semibold text-gray-900">
            No total inventory items available for{" "}
            <span className="text-[#7F58E6]">
              {employeeData?.fullName || "this employee"}
            </span>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-gray-50">
      <div className="max-w-5xl mx-auto bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between border-b border-gray-200 bg-gray-50">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Total Inventory Summary
            </p>
            <p className="text-lg font-semibold text-gray-900">
              {employeeData?.fullName || "Employee"}
            </p>
          </div>
          <div className="mt-3 sm:mt-0 text-sm text-gray-500 flex flex-col items-start sm:items-end">
            <span className="font-medium">
              {inventories.length} item
              {inventories.length > 1 ? "s" : ""} tracked
            </span>
          </div>
        </div>

        {/* Body */}
        <div className="p-4 sm:p-6 space-y-4">
          {/* Filters row */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            {/* Search by name */}
            <div className="w-full sm:w-72">
              <input
                type="text"
                value={search}
                onChange={handleSearchChange}
                placeholder="Search by name..."
                className="w-full px-3 py-2 text-sm rounded-xl bg-gray-50 border border-gray-300 text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-[#7F58E6]"
              />
            </div>

            {/* Stock filter */}
            <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
              <span className="text-gray-500">Filter by stock:</span>
              <div className="inline-flex rounded-xl border border-gray-300 bg-gray-50 overflow-hidden">
                {["ALL", "IN_STOCK", "LOW_STOCK", "OUT_OF_STOCK"].map(
                  (value) => {
                    const isActive = stockFilter === value;
                    const labelMap = {
                      ALL: "All",
                      IN_STOCK: "In Stock",
                      LOW_STOCK: "Low Stock",
                      OUT_OF_STOCK: "Out of Stock",
                    };
                    return (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setStockFilter(value)}
                        className={`px-3 py-1.5 whitespace-nowrap border-r border-gray-300 last:border-r-0 ${isActive
                          ? "bg-[#7F58E6] text-white"
                          : "bg-transparent text-gray-600 hover:bg-gray-100"
                          }`}
                      >
                        {labelMap[value]}
                      </button>
                    );
                  }
                )}
              </div>
            </div>
          </div>

          {/* Grid of inventory cards */}
          {filteredInventories.length === 0 ? (
            <div className="text-center text-sm text-gray-500 py-8">
              No inventory items match the current search / filter.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {filteredInventories.map((inventory) => {
                const status = inventory.stockStatus;
                const statusClasses = getStatusClasses(status);

                return (
                  <div
                    key={inventory.id}
                    onClick={() => {
                      setSelectedCategory(inventory);
                      setIsDrawerOpen(true);
                    }}
                    className="border border-gray-200 rounded-xl p-4 sm:p-5 flex flex-col gap-3 bg-white cursor-pointer hover:bg-gray-50 transition"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-gray-900">
                          {inventory.name || "Unnamed Inventory"}
                        </p>
                      </div>
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full border text-xs font-medium ${statusClasses}`}
                      >
                        {getStatusLabel(status)}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-3 text-xs sm:text-sm text-gray-600">
                      <div>
                        <p className="text-gray-500 text-xs">Items</p>
                        <p className="font-semibold text-gray-900">
                          {inventory.itemCount}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">Parts / Item</p>
                        <p className="font-semibold text-gray-900">
                          {inventory.partsPerItem}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">Parts Available</p>
                        <p className="font-semibold text-gray-900">
                          {inventory.availableParts}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">Used Parts</p>
                        <p className="font-semibold text-gray-900">
                          {inventory.usage}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">
                          Min Stock (Parts)
                        </p>
                        <p className="font-semibold text-gray-900">
                          {inventory.minStock}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <PendingInventoryDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        categoryId={selectedCategory?.id}
        categoryName={selectedCategory?.name}
        employeeId={employeeData?.id}
        status="Assigned"
        onlyUsable={true}
      />
    </div>
  );
};

export default InventoryTotalSummary;
